import { useState, useMemo } from 'react';
import "../../Styles/contrastGrid.scss";
import { CloseButton } from '../ReusableComponents/PopupDefault';

// WCAG 2.1 contrast ratio calculation
function getLuminance(r, g, b) {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getContrastRatio(color1, color2) {
  const lum1 = getLuminance(...color1);
  const lum2 = getLuminance(...color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

// Simplified APCA calculation (approximation)
function getAPCAContrast(textColor, bgColor) {
  const textLum = getLuminance(...textColor);
  const bgLum = getLuminance(...bgColor);

  // Simplified APCA calculation - this is an approximation
  const lighter = Math.max(textLum, bgLum);
  const darker = Math.min(textLum, bgLum);

  if (lighter === textLum) {
    return Math.abs((lighter ** 0.56 - darker ** 0.57) * 1.14) * 100;
  } else {
    return Math.abs((lighter ** 0.65 - darker ** 0.62) * 1.14) * 100;
  }
}

// Helper to convert hex to RGB
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
}


// Sample color scale - replace with your actual colors
const defaultColors = [
  { name: '50', hex: '#fafafa' },
  { name: '100', hex: '#f4f4f5' },
  { name: '200', hex: '#e4e4e7' },
  { name: '300', hex: '#d4d4d8' },
  { name: '400', hex: '#a1a1aa' },
  { name: '500', hex: '#71717a' },
  { name: '600', hex: '#52525b' },
  { name: '700', hex: '#3f3f46' },
  { name: '800', hex: '#27272a' },
  { name: '900', hex: '#18181b' },
  { name: '950', hex: '#09090b' }
];

export default function ContrastGrid({ colors = defaultColors, onClose }) {
  const [wcagStandard, setWcagStandard] = useState('wcag2');
  const [filter, setFilter] = useState('all');
  const [showRating, setShowRating] = useState(false);


  const cellMeetsFilter = (cell) => {
    if (filter === 'all') return true;

    if (wcagStandard === 'wcag2') {
      switch (filter) {
        case '3+': return cell.wcag2Ratio >= 3;
        case '4.5+': return cell.wcag2Ratio >= 4.5;
        case '7+': return cell.wcag2Ratio >= 7;
        default: return true;
      }
    } else {
      switch (filter) {
        case '15+': return cell.apcaScore >= 15;
        case '30+': return cell.apcaScore >= 30;
        case '45+': return cell.apcaScore >= 45;
        case '60+': return cell.apcaScore >= 60;
        case '75+': return cell.apcaScore >= 75;
        default: return true;
      }
    }
  };

  // Add pure white and black to the color palette
  const extendedColors = useMemo(() => {
    return [
      { name: 'White', hex: '#ffffff' },
      ...colors,
      { name: 'Black', hex: '#000000' }
    ];
  }, [colors]);

  const contrastData = useMemo(() => {
    return extendedColors.map(textColor =>
      extendedColors.map(bgColor => {
        const textRgb = hexToRgb(textColor.hex);
        const bgRgb = hexToRgb(bgColor.hex);
        const wcag2Ratio = getContrastRatio(textRgb, bgRgb);
        const apcaScore = getAPCAContrast(textRgb, bgRgb);

        return {
          textColor,
          bgColor,
          wcag2Ratio,
          apcaScore,
          textRgb,
          bgRgb
        };
      })
    );
  }, [extendedColors]);

  const getContrastStatus = (cell) => {
    if (wcagStandard === 'wcag2') {
      if (cell.wcag2Ratio >= 7) return 'excellent';
      if (cell.wcag2Ratio >= 4.5) return 'good';
      if (cell.wcag2Ratio >= 3) return 'fair';
      return 'poor';
    } else {
      if (cell.apcaScore >= 75) return 'excellent';
      if (cell.apcaScore >= 60) return 'good';
      if (cell.apcaScore >= 45) return 'fair';
      if (cell.apcaScore >= 30) return 'warning';
      return 'poor';
    }
  };

  const wcag2Filters = ['all', '3+', '4.5+', '7+'];
  const wcag3Filters = ['all', '15+', '30+', '45+', '60+', '75+'];

  return (
    <div className="contrast-grid-popup">

      {/* row top */}
      <div className="row row--top">

        {/* row 1 */}
        <div className="row-item row-item--1">
          Contrast grid
        </div>

        {/* row 2 */}
        <div className="row-item row-item--2">
          <span className="controls-label">Standard:</span>

          <div
            className={`row-option ${wcagStandard === 'wcag2' ? 'active' : ''}`}
            onClick={() => {
              setWcagStandard('wcag2');
              setFilter('all');
            }}
          >
            <p className="mode">WCAG 2</p>
          </div>

          <div
            className={`row-option ${wcagStandard === 'wcag3' ? 'active' : ''}`}
            onClick={() => {
              setWcagStandard('wcag3');
              setFilter('all');
            }}
          >
            <p className="mode">WCAG 3 (APCA)</p>
          </div>
        </div>
      </div>


      {/* row bottom */}
      <div className="row row--bottom">

        {/* row 3: filters */}
        <div className='row-item row-item--3'>
          <span className="controls-label">
            Filter by {wcagStandard === 'wcag2' ? 'contrast ratio' : 'APCA score'}:
          </span>

          <div
            className={`row-option ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            <p className="mode">All</p>
          </div>

          {(wcagStandard === 'wcag2' ? wcag2Filters : wcag3Filters)
            .filter(f => f !== 'all')
            .map(f => (
              <div
                key={f}
                className={`row-option ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                <p className="mode">{f}</p>
              </div>
            ))
          }

          {/* info */}
          <div className="info">
            {wcagStandard === 'wcag2' ? (
              <p className="info-content">
                Values ≥4.5 meet AA standard, ≥7.0 meet AAA standard
              </p>
            )
              : (
                <p className="info-content">
                  Higher values indicate better readability. 60+ recommended for body text.
                </p>
              )
            }
          </div>
        </div>

        {/* row 4: grid */}
        <div className="row-item row-item--4">
          {/* the actual grid */}

          <div className="contrast-grid-container">
            <div className="grid">
              {/* Header row */}
              <div className="row">
                <div className="cell cell--header"></div>
                {extendedColors.map(color => (
                  <div
                    key={color.name}
                    className="cell cell--header"
                  >
                    <span className='cell-text'>
                      {color.name}
                    </span>
                  </div>
                ))}
              </div>

              {/* Data rows */}
              {contrastData.map((row, rowIndex) => (
                <div key={rowIndex} className="row">
                  <div className="cell cell--header">
                    <span className='cell-text'>
                      {extendedColors[rowIndex].name}
                    </span>
                  </div>
                  {extendedColors.map((bgColor, colIndex) => {
                    const cell = contrastData[rowIndex][colIndex];
                    const status = getContrastStatus(cell);
                    const meetsFilter = cellMeetsFilter(cell);

                    if (!meetsFilter) {
                      return (
                        <div
                          key={colIndex}
                          className="cell cell--hidden"
                        />
                      );
                    }

                    return (
                      <div
                        key={colIndex}
                        className={`cell cell--${showRating && status}`}
                        style={{ backgroundColor: bgColor.hex }}
                        title={`${wcagStandard === 'wcag2' ? `WCAG 2: ${cell.wcag2Ratio.toFixed(1)}` : `APCA: ${cell.apcaScore.toFixed(0)}`}`}
                      >
                        <span
                          className={`cell-text ${getLuminance(...cell.textRgb) > 0.5 ? 'cell-text--dark' : 'cell-text--light'}`}
                          style={{ color: extendedColors[rowIndex].hex }}
                        >
                          {wcagStandard === 'wcag2'
                            ? cell.wcag2Ratio.toFixed(1)
                            : cell.apcaScore.toFixed(0)
                          }
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>



          <div className="rating-and-legend">


            {/* rating button */}
            <button className='rating-btn pri-btn svg---text' onClick={() => setShowRating(!showRating)}>
              {
                showRating ? (
                  <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#ffffff">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (

                  <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#ffffff">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                )
              }
              <span>Rating</span>
            </button>

            {/* legend */}
            {showRating &&
              <div className="legend">
                <div className="legend-items">
                  <div className="legend-item">
                    <div className="legend-color legend--excellent"></div>
                    <span>Excellent</span>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color legend--good"></div>
                    <span>Good</span>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color legend--fair"></div>
                    <span>Fair</span>
                  </div>

                  {wcagStandard === 'wcag3' && (
                    <div className="legend-item">
                      <div className="legend-color legend--warning"></div>
                      <span>Warning</span>
                    </div>
                  )}

                  <div className="legend-item">
                    <div className="legend-color legend--poor"></div>
                    <span>Poor</span>
                  </div>
                </div>
              </div>
            }
          </div>

        </div>

      </div>

      <CloseButton onClose={onClose} />

    </div>
  );
}